/** Profile save + encrypted sync — display name, bio and avatar (photo/GIF/video).
 * Updates persist locally (sealed at rest) and broadcast as CONTENT_TYPE_PROFILE
 * envelopes to every 1:1 contact over the existing ratchet sessions (SPEC §5.1, §9). */

import type { Account, Contact } from "@veto/domain";
import {
  CONTENT_TYPE_PROFILE,
  MAX_ABOUT_CHARS,
  MAX_PROFILE_NAME_CHARS,
  buildProfileAvatarWire,
  encodeProfilePayload,
  type ProfileMedia,
  type ProfileWirePayload,
} from "@veto/protocol";
import { listConversations, saveAccount } from "@veto/storage";
import { profileMediaToBlob } from "../profile/profileMedia";
import type { Messenger } from "../messaging/messenger";

export type ProfileInput = {
  displayName: string;
  about: string;
  avatar: ProfileMedia | null;
};

export function createProfileActions(deps: {
  messenger: Messenger | null;
  account: Account | null;
  contacts: Contact[];
  setAccount: (a: Account | null) => void;
  setToast: (t: string | null) => void;
  refresh: () => Promise<void>;
}) {
  async function saveProfile(input: ProfileInput): Promise<void> {
    if (!deps.account) return;
    const displayName = input.displayName.trim().slice(0, MAX_PROFILE_NAME_CHARS);
    if (!displayName) {
      deps.setToast("Display name can't be empty");
      return;
    }
    const about = input.about.trim().slice(0, MAX_ABOUT_CHARS);

    // 1. Persist locally first (offline-first; sync is best-effort).
    const next: Account = { ...deps.account, displayName, about, avatar: input.avatar };
    await saveAccount(next);
    deps.setAccount(next);
    await deps.refresh();

    // 2. Build the wire payload — avatar ciphertext is prepared once and shared
    // by every envelope; the file key still travels per-conversation E2EE only.
    let avatarWire: ProfileWirePayload["avatar"] = null;
    let avatarSkipped = false;
    if (input.avatar) {
      try {
        const { avatar } = await buildProfileAvatarWire({
          blob: profileMediaToBlob(input.avatar),
          mime: input.avatar.mime,
          kind: input.avatar.kind,
        });
        avatarWire = avatar;
      } catch {
        avatarSkipped = true;
      }
    }
    const payload: ProfileWirePayload = {
      op: "profile",
      v: 1,
      displayName,
      about,
      updatedAt: Date.now(),
      avatar: avatarWire,
    };
    let body: string;
    try {
      body = encodeProfilePayload(payload);
    } catch {
      deps.setToast(avatarSkipped || avatarWire ? "Profile saved locally — photo too large to sync" : "Profile saved locally");
      return;
    }

    // 3. Fan out one encrypted profile update per contact conversation.
    const conversations = await listConversations();
    let sent = 0;
    let failed = 0;
    for (const contact of deps.contacts) {
      if (!deps.messenger) break;
      // Profile goes to accepted contacts only — never to pending requests.
      if (contact.blockedAt || contact.requestState === "pending") continue;
      const conv = conversations.find((c) => c.contactId === contact.id);
      if (!conv) continue;
      try {
        await deps.messenger.send({
          conversationId: conv.id,
          recipients: [contact],
          contentType: CONTENT_TYPE_PROFILE,
          body,
        });
        sent += 1;
      } catch {
        failed += 1;
      }
    }

    if (failed > 0) {
      deps.setToast(
        avatarSkipped
          ? `Profile saved — photo will sync next update (${failed} contact(s) pending)`
          : `Profile saved — sync pending for ${failed} contact(s)`,
      );
    } else if (sent > 0) {
      deps.setToast(
        avatarSkipped ? "Profile updated — photo will sync next update" : "Profile updated — synced encrypted to contacts",
      );
    } else {
      deps.setToast("Profile saved");
    }
  }

  return { saveProfile };
}
